import { Component, h, State, Fragment } from '@stencil/core';
import { getAuthClient, AuthClientInstance } from '@trumpf/ux.utils';
import { addChat, deleteAllChats, fetchCases, fetchChats, getCaseStatus, ICase, IChat } from '../../backend/chatService';

@Component({
  tag: 'chat-manager',
  styleUrl: 'chat-manager.scss',
  shadow: true,
})
export class ChatManager {
  private authClient: AuthClientInstance;

  @State() token: string | null = null;
  @State() cases: ICase[] = [];
  @State() selectedCaseId: string | undefined = undefined;
  @State() selectedChats: IChat[] = [];
  @State() loadingChats: boolean = false;
  @State() statuses: { [caseId: string]: { status: string; hasUserChat: boolean; hasTechnicanChat: boolean } } = {};
  @State() selectedChat: IChat | null = null;
  @State() isMinimized: boolean = false;

  constructor() {
    this.authClient = getAuthClient();
  }

  async componentWillLoad() {
    await this.retrieveToken();
  }

  async componentDidLoad() {
    await this.loadCases();
  }

  private async retrieveToken() {
    try {
      // First check sessionStorage
      let token = sessionStorage.getItem('auth_token');

      // If no token in session, try auth client
      if (!token) {
        token = this.authClient.getAccessToken();

        if (!token) {
          console.warn('Token is not available yet. Waiting for initialization...');
          token = await this.waitForToken();
        }
      }

      if (token) {
        console.log('Token retrieved successfully');
        this.token = token;
        sessionStorage.setItem('auth_token', token);

        // Subscribe to auth client changes to keep token up to date
        this.authClient.subscribe(() => {
          const newToken = this.authClient.getAccessToken();
          if (newToken !== this.token) {
            this.token = newToken;
            sessionStorage.setItem('auth_token', newToken);
          }
        });
      } else {
        console.warn('Token is still unavailable after waiting.');
      }
    } catch (error) {
      console.error('Error retrieving access token:', error);
    }
  }

  private async waitForToken(maxRetries = 10, delayMs = 200): Promise<string | null> {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      const token = this.authClient.getAccessToken();
      if (token) {
        return token;
      }
      await new Promise(resolve => setTimeout(resolve, delayMs)); // Delay before next attempt
    }
    return null;
  }

  async loadCases() {
    try {
      this.cases = await fetchCases();
    } catch (error) {
      console.error('Error loading cases:', error);
    }
  }

  async loadChats(caseId: string) {
    try {
      this.loadingChats = true;
      this.selectedChats = await fetchChats(caseId);
      this.selectedCaseId = caseId;
    } catch (error) {
      console.error(`Error loading chats for case ${caseId}:`, error);
    } finally {
      this.loadingChats = false;
    }
  }

  private async handleAddChat(caseId: string | undefined) {
    if (!caseId) {
      console.warn('No case selected for adding a chat.');
      return;
    }

    try {
      const newChat = await addChat(caseId, 'Technican', 'Technican'); // Example values for author and group
      this.selectedChats = [...this.selectedChats, newChat];
    } catch (error) {
      console.error('Error adding new chat:', error);
    }
  }

  private async handleDeleteAllChats(caseId: string | undefined) {
    if (!caseId) {
      console.warn('No case selected for deleting chats.');
      return;
    }

    try {
      console.log(`Deleting all chats for case: ${caseId}`);
      await deleteAllChats(caseId);

      // Clear the selected chats in the local state
      this.selectedChats = [];
      console.log('All chats deleted successfully.');
    } catch (error) {
      console.error(`Error deleting all chats for case ${caseId}:`, error);
    }
  }

  private async handleGetCaseStatus(caseId: string | undefined) {
    if (!caseId) {
      console.warn('No case selected to fetch status.');
      return;
    }

    try {
      console.log(`Fetching status for case: ${caseId}`);
      const status = await getCaseStatus(caseId);
      this.statuses = { ...this.statuses, [caseId]: status }; // Update status for the specific case
      console.log('Case status updated:', this.statuses);
    } catch (error) {
      console.error(`Error fetching status for case ${caseId}:`, error);
    }
  }

  private handleSelectChat(sessionId: string) {
    this.selectedChat = this.selectedChats.find(chat => chat.sessionId === sessionId);
    if (!this.selectedChat) {
      console.warn(`Chat with sessionId ${sessionId} not found.`);
      return;
    }
  }

  private toggleMinimize = () => {
    this.isMinimized = !this.isMinimized;
  };

  render() {
    return (
      <div
        class={{
          'chat-manager': true,
          'minimized': this.isMinimized,
        }}
      >
        <div class="window-controls">
          <div class="title">Chat Manager</div>
          <ux-button variant="no-border" size="icon" icon={this.isMinimized ? 'maximize' : 'minimize'} onClick={this.toggleMinimize}>
            {this.isMinimized ? 'Maximize' : 'Minimize'}
          </ux-button>
        </div>

        {!this.isMinimized && (
          <div class="content">
            {/* Cases Section */}
            <div class="cases-header">
              <h2>Cases</h2>
            </div>

            {this.cases.length === 0 ? (
              <div class="empty-state">
                <ux-icon name="folder" size="small"></ux-icon>
                <p>No cases available</p>
              </div>
            ) : (
              <ul class="cases-list">
                {this.cases.map(caseItem => (
                  <li
                    key={caseItem.caseId}
                    class={{
                      'case-item': true,
                      'active': this.selectedCaseId === caseItem.caseId,
                    }}
                    onClick={() => this.loadChats(caseItem.caseId)}
                  >
                    <span class="case-title">Case #{caseItem.caseId}</span>
                    <div class="case-status">
                      <ux-button
                        size="icon"
                        variant="no-border"
                        icon="info"
                        onClick={e => {
                          e.stopPropagation();
                          this.handleGetCaseStatus(caseItem.caseId);
                        }}
                      ></ux-button>
                      {this.statuses[caseItem.caseId] && (
                        <ux-badge variant={this.statuses[caseItem.caseId].status === 'open' ? 'success' : 'neutral'}>{this.statuses[caseItem.caseId].status}</ux-badge>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {/* Chats Section */}
            <div class="chats-section">
              {this.selectedCaseId ? (
                <>
                  <div class="chats-header">
                    <h2>Chats</h2>
                    <div class="actions">
                      <ux-button variant="primary" size="small" icon="plus" onClick={() => this.handleAddChat(this.selectedCaseId)}>
                        New
                      </ux-button>
                      <ux-button variant="secondary" size="small" icon="minus" onClick={() => this.handleDeleteAllChats(this.selectedCaseId)}>
                        Delete All
                      </ux-button>
                    </div>
                  </div>

                  <div class="chat-list">
                    {this.loadingChats ? (
                      <div class="loading-indicator">
                        <ux-loader variant="horizontal" size="small"></ux-loader>
                      </div>
                    ) : this.selectedChats.length === 0 ? (
                      <div class="empty-state">
                        <ux-icon name="chat" size="small"></ux-icon>
                        <p>No chats available</p>
                      </div>
                    ) : (
                      <ul class="chat-list">
                        {this.selectedChats.map(chat => (
                          <li
                            key={chat.sessionId}
                            class={{
                              'chat-list-item': true,
                              'active': this.selectedChat?.sessionId === chat.sessionId,
                            }}
                            onClick={() => this.handleSelectChat(chat.sessionId)}
                          >
                            <h4>
                              <ux-icon name={chat.author === 'Technican' ? 'support' : 'user'} size="small"></ux-icon>
                              {chat.author}
                            </h4>
                            <div class="chat-meta">ID: {chat.sessionId}</div>
                            <div class="chat-preview">{chat.messages?.length || 0} messages</div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </>
              ) : (
                <div class="empty-state">
                  <ux-icon name="folder-open" size="small"></ux-icon>
                  <p>Select a case to view chats</p>
                </div>
              )}
            </div>

            {/* Debug Panel */}
            {this.selectedChat && (
              <div class="debug-panel">
                <h3>Debug Info</h3>
                <pre>
                  {JSON.stringify(
                    {
                      caseId: this.selectedCaseId,
                      sessionId: this.selectedChat.sessionId,
                      author: this.selectedChat.author,
                      messages: this.selectedChat.messages?.length || 0,
                    },
                    null,
                    2,
                  )}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}
